import * as React from "react"
import { ChevronDown, Plus, type LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Badge } from "@/shared/ui/atoms/Badge"
import { Button } from "@/shared/ui/atoms/Button"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/atoms/Card"

interface CollectionSectionProps {
  title: string
  description: string
  count?: number
  icon: LucideIcon
  actionLabel?: string
  onAction?: () => void
  children: React.ReactNode
  defaultOpen?: boolean
  isFetching?: boolean
  className?: string
  contentClassName?: string
}

export function CollectionSection({
  title,
  description,
  count,
  icon: Icon,
  actionLabel,
  onAction,
  children,
  defaultOpen = true,
  isFetching = false,
  className,
  contentClassName,
}: CollectionSectionProps) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen)
  const sectionId = React.useId()

  return (
    <Card className={cn("cartera-collection overflow-hidden py-0 border border-border/80 shadow-xs relative", className)}>
      {isFetching && (
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-primary/20 overflow-hidden z-20">
          <div className="h-full bg-primary animate-pulse w-full" />
        </div>
      )}
      <CardHeader className="cartera-collection-header border-b px-4 py-3 sm:px-5">
        <div className="flex min-w-0 items-start gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-auto min-w-0 justify-start px-0 py-0.5 text-left hover:bg-transparent"
            onClick={() => setIsOpen((open) => !open)}
            aria-expanded={isOpen}
            aria-controls={sectionId}
          >
            <ChevronDown
              data-icon="inline-start"
              className={cn("transition-transform duration-200", !isOpen ? "-rotate-90" : "")}
            />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="flex items-center gap-2">
                <Icon className="cartera-collection-icon" />
                <CardTitle className="text-sm sm:text-base font-semibold">{title}</CardTitle>
                {count !== undefined && (
                  <Badge variant="secondary" className="px-1.5 py-0 text-[11px] font-mono">{count}</Badge>
                )}
              </span>
              <CardDescription className="hidden sm:block text-xs">{description}</CardDescription>
            </span>
          </Button>
        </div>
        {actionLabel && onAction && (
          <CardAction>
            <Button variant="ghost" size="sm" onClick={onAction} className="h-8 text-xs sm:text-sm">
              <Plus data-icon="inline-start" className="size-3.5" />
              <span className="hidden xs:inline">{actionLabel}</span>
              <span className="xs:hidden">Nuevo</span>
            </Button>
          </CardAction>
        )}
      </CardHeader>
      {isOpen ? (
        <CardContent id={sectionId} className={cn("cartera-collection-content", contentClassName || "p-0")}>
          {children}
        </CardContent>
      ) : null}
    </Card>
  )
}

